import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IoArrowBack,
  IoSaveOutline,
  IoAdd,
  IoTrashOutline,
  IoRestaurantOutline,
  IoNutritionOutline,
  IoFlaskOutline,
} from 'react-icons/io5';
import api from '../api';
import { toast } from 'react-hot-toast';

const MEAL_PRESETS = ['Desayuno', 'Almuerzo', 'Merienda', 'Cena', 'Pre-entreno', 'Post-entreno', 'Snack'];
const UNITS = ['g', 'ml', 'ud', 'cucharada', 'taza', 'porción'];
const SUPP_TIMING = ['Mañana', 'Con desayuno', 'Media mañana', 'Pre-entreno', 'Post-entreno', 'Con almuerzo', 'Con cena', 'Antes de dormir', 'Con comida'];
const emptySupp = () => ({ name: '', dose: '', timing: 'Mañana', notes: '' });

const emptyItem = () => ({ food_name: '', quantity: '', unit: 'g', calories: '', protein_g: '', carbs_g: '', fat_g: '' });
const emptyMeal = (order) => ({ name: 'Desayuno', meal_order: order, notes: '', items: [emptyItem()] });
const emptyDay = (order) => ({ name: `Día ${order}`, day_number: order, is_active: true, meals: [emptyMeal(1)] });

const NutritionPlanEdit = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [planName, setPlanName] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [days, setDays] = useState([]);
  const [expandedDay, setExpandedDay] = useState(0);
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await api.get(`/nutrition-plans/${planId}`);
        const plan = res.data;
        setPlanName(plan.name);
        setDescription(plan.description || '');
        setGoal(plan.goal || '');
        setDays(plan.daily_plans.map(day => ({
          ...day,
          meals: day.meals.map(meal => ({
            ...meal,
            items: meal.items || []
          }))
        })));
        setSupplements((plan.supplements || []).map(s => ({
          name: s.name || '',
          dose: s.dose || '',
          timing: s.timing || 'Mañana',
          notes: s.notes || ''
        })));
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Error al cargar el plan');
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planId]);

  // --- Handlers de DÍAS ---
  const addDay = () => {
    const newDays = [...days, emptyDay(days.length + 1)];
    setDays(newDays);
    setExpandedDay(newDays.length - 1);
  };
  const removeDay = (di) => setDays(days.filter((_, i) => i !== di).map((d, i) => ({ ...d, day_number: i + 1, name: d.name.startsWith('Día ') ? `Día ${i + 1}` : d.name })));
  const updateDay = (di, field, value) => { const d = [...days]; d[di] = { ...d[di], [field]: value }; setDays(d); };

  // --- Handlers de COMIDAS ---
  const addMeal = (di) => { 
    const d = [...days]; 
    d[di].meals.push(emptyMeal(d[di].meals.length + 1)); 
    setDays(d); 
  };
  const removeMeal = (di, mi) => { 
    const d = [...days]; 
    d[di].meals = d[di].meals.filter((_, i) => i !== mi).map((m, i) => ({ ...m, meal_order: i + 1 })); 
    setDays(d); 
  };
  const updateMeal = (di, mi, field, value) => { const d = [...days]; d[di].meals[mi] = { ...d[di].meals[mi], [field]: value }; setDays(d); };

  // --- Handlers de SUPLEMENTOS ---
  const addSupp = () => setSupplements([...supplements, emptySupp()]);
  const removeSupp = (si) => setSupplements(supplements.filter((_, i) => i !== si));
  const updateSupp = (si, field, value) => { const s = [...supplements]; s[si] = { ...s[si], [field]: value }; setSupplements(s); };

  // --- Handlers de ITEMS ---
  const addItem = (di, mi) => { const d = [...days]; d[di].meals[mi].items.push(emptyItem()); setDays(d); };
  const removeItem = (di, mi, ii) => { const d = [...days]; d[di].meals[mi].items = d[di].meals[mi].items.filter((_, i) => i !== ii); setDays(d); };
  const updateItem = (di, mi, ii, field, value) => { 
    const d = [...days]; 
    d[di].meals[mi].items[ii] = { ...d[di].meals[mi].items[ii], [field]: value }; 
    setDays(d); 
  };

  const calculateDayTotals = (day) => {
    let cal = 0, pro = 0, cho = 0, fat = 0;
    if (day.meals) {
      day.meals.forEach(m => {
        if (m.items) {
          m.items.forEach(i => {
            cal += parseFloat(i.calories) || 0;
            pro += parseFloat(i.protein_g) || 0;
            cho += parseFloat(i.carbs_g) || 0;
            fat += parseFloat(i.fat_g) || 0;
          });
        }
      });
    }
    return { cal, pro, cho, fat };
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!planName) return alert('Nombre de plan requerido');
    setSaving(true);
    setError('');

    const cleanedDays = days.map((day, dayIndex) => ({
      day_number: day.day_number || dayIndex + 1,
      name: day.name || `Día ${dayIndex + 1}`,
      meals: (day.meals || []).map((meal, mealIndex) => ({
        name: meal.name || `Comida ${mealIndex + 1}`,
        meal_order: meal.meal_order || mealIndex + 1,
        notes: meal.notes || null,
        items: (meal.items || []).map((item) => ({
          food_name: item.food_name || '',
          quantity: item.quantity === '' || item.quantity == null ? null : parseFloat(item.quantity),
          unit: item.unit || null,
          calories: item.calories === '' || item.calories == null ? null : parseFloat(item.calories),
          protein_g: item.protein_g === '' || item.protein_g == null ? null : parseFloat(item.protein_g),
          carbs_g: item.carbs_g === '' || item.carbs_g == null ? null : parseFloat(item.carbs_g),
          fat_g: item.fat_g === '' || item.fat_g == null ? null : parseFloat(item.fat_g),
        }))
      }))
    }));

    try {
      await api.put(`/nutrition-plans/${planId}`, {
        name: planName,
        description,
        goal,
        daily_plans: cleanedDays,
        supplements: supplements.filter(s => s.name.trim())
      });
      toast.success('Plan actualizado correctamente');
      navigate('/nutrition-plans');
    } catch (err) {
      console.error(err);
      const backendMsg = err.response?.data?.detail;
      const msg = Array.isArray(backendMsg)
        ? backendMsg.map((d) => d.msg).join(' | ')
        : (backendMsg || 'Error al guardar el plan');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-300 font-medium">Cargando plan...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 text-gray-100">
      {/* Header & Main Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-400 hover:text-white mb-2 transition-colors">
            <IoArrowBack className="mr-1" /> Volver
          </button>
          <h1 className="text-3xl font-extrabold text-white">Editar {planName}</h1>
        </div>
        <button 
          type="button"
          onClick={handleSave} 
          disabled={saving}
          className="bg-marizan-red hover:bg-red-700 text-white px-6 py-3 rounded-xl shadow-lg flex items-center justify-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50"
        >
          {saving ? <span className="animate-spin mr-2 text-white">●</span> : <IoSaveOutline className="mr-2" size={20} />}
          {saving ? 'Guardando...' : 'Actualizar Plan'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="glass-panel p-6 rounded-2xl border border-white/10">
            <h2 className="text-lg font-semibold mb-4 text-white flex items-center">
              <IoNutritionOutline className="mr-2 text-marizan-red" /> Información General
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre del Plan</label>
                <input 
                  type="text" value={planName} onChange={(e) => setPlanName(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red focus:border-transparent transition-all outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Objetivo</label>
                <input 
                  type="text" value={goal} onChange={(e) => setGoal(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red focus:border-transparent transition-all outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Descripción</label>
                <textarea 
                  rows="3" value={description} onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-marizan-red focus:border-transparent transition-all outline-none"
                />
              </div>
            </div>
          </div>

          <div className="glass-panel p-6 rounded-2xl border border-white/10">
            <h2 className="text-lg font-semibold mb-4 text-white flex items-center">
              <IoRestaurantOutline className="mr-2 text-marizan-red" /> Resumen de Días
            </h2>
            <div className="space-y-3">
              {days.map((day, di) => {
                const totals = calculateDayTotals(day);
                return (
                  <div 
                    key={di} 
                    onClick={() => setExpandedDay(di)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all ${expandedDay === di ? 'bg-marizan-red/10 border-marizan-red/40 shadow-sm' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-bold ${expandedDay === di ? 'text-marizan-red' : 'text-gray-200'}`}>{day.name}</span>
                      <span className="text-xs font-semibold text-gray-500">{Math.round(totals.cal)} kcal</span>
                    </div>
                    <div className="flex gap-2 text-[10px] uppercase tracking-wider">
                      <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">P: {Math.round(totals.pro)}g</span>
                      <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">H: {Math.round(totals.cho)}g</span>
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">G: {Math.round(totals.fat)}g</span>
                    </div>
                  </div>
                );
              })}
              <button 
                onClick={addDay}
                className="w-full py-2 border-2 border-dashed border-white/20 rounded-xl text-gray-300 text-sm font-medium hover:bg-white/10 hover:border-marizan-red/40 hover:text-white transition-all flex items-center justify-center p-4 mt-2"
              >
                <IoAdd className="mr-1" /> Añadir día
              </button>
            </div>
          </div>

          {/* Supplements panel */}
          <div className="glass-panel p-6 rounded-2xl border border-white/10">
            <h2 className="text-lg font-semibold mb-4 text-white flex items-center">
              <IoFlaskOutline className="mr-2 text-marizan-red" /> Suplementos
            </h2>
            <div className="space-y-3">
              {supplements.map((s, si) => (
                <div key={si} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text" value={s.name} onChange={(e) => updateSupp(si, 'name', e.target.value)}
                      placeholder="Nombre del suplemento"
                      className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                    />
                    <button onClick={() => removeSupp(si)} className="text-red-400 hover:text-red-300 p-1.5">
                      <IoTrashOutline size={16} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text" value={s.dose} onChange={(e) => updateSupp(si, 'dose', e.target.value)}
                      placeholder="Dosis (ej: 5g, 2 cáps)"
                      className="w-1/2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                    />
                    <select
                      value={s.timing} onChange={(e) => updateSupp(si, 'timing', e.target.value)}
                      className="w-1/2 px-3 py-1.5 bg-gray-800 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                    >
                      {SUPP_TIMING.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <input
                    type="text" value={s.notes} onChange={(e) => updateSupp(si, 'notes', e.target.value)}
                    placeholder="Notas opcionales"
                    className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                  />
                </div>
              ))}
              <button
                onClick={addSupp}
                className="w-full py-2 border-2 border-dashed border-white/20 rounded-xl text-gray-300 text-sm font-medium hover:bg-white/10 hover:border-marizan-red/40 hover:text-white transition-all flex items-center justify-center p-3 mt-1"
              >
                <IoAdd className="mr-1" /> Añadir suplemento
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {days.map((day, di) => (
            expandedDay === di && (
              <div key={di} className="animate-fadeIn">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                  <div className="flex-1">
                    <input 
                      type="text" value={day.name} onChange={(e) => updateDay(di, 'name', e.target.value)}
                      className="text-2xl font-bold text-white bg-transparent border-none p-0 focus:ring-0 w-full"
                    />
                  </div>
                  <button onClick={() => removeDay(di)} className="text-red-500 hover:text-red-700 text-sm flex items-center">
                    <IoTrashOutline className="mr-1" /> Eliminar día
                  </button>
                </div>

                <div className="space-y-6">
                  {day.meals.map((meal, mi) => (
                    <div key={mi} className="glass-panel rounded-2xl border border-white/10 overflow-hidden">
                      <div className="bg-white/5 px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between border-b border-white/10 gap-4">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 flex-1 w-full md:w-auto">
                          <select
                            value={MEAL_PRESETS.includes(meal.name) ? meal.name : '__custom__'}
                            onChange={(e) => updateMeal(di, mi, 'name', e.target.value === '__custom__' ? '' : e.target.value)}
                            className="bg-gray-800 border border-white/15 text-gray-200 rounded-lg px-3 py-1.5 font-bold text-sm focus:ring-2 focus:ring-marizan-red outline-none"
                          >
                            {MEAL_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                            {!MEAL_PRESETS.includes(meal.name) && <option value="__custom__">{meal.name}</option>}
                            <option value="__custom__">Otro...</option>
                          </select>
                          
                          {(!MEAL_PRESETS.includes(meal.name) || meal.name === '') && (
                            <input 
                              type="text" value={meal.name} onChange={(e) => updateMeal(di, mi, 'name', e.target.value)}
                              placeholder="Nombre de la comida"
                              className="bg-gray-800 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-marizan-red outline-none flex-1"
                            />
                          )}
                        </div>
                        <button onClick={() => removeMeal(di, mi)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <IoTrashOutline size={18} />
                        </button>
                      </div>

                      <div className="p-6">
                        <div className="space-y-4">
                          {meal.items.map((item, ii) => (
                            <div key={ii} className="group relative bg-white/5 border border-white/10 rounded-xl p-4 transition-all hover:shadow-md hover:border-marizan-red/30">
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                <div className="md:col-span-5">
                                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1 leading-none">Alimento</label>
                                  <input 
                                    type="text" value={item.food_name} onChange={(e) => updateItem(di, mi, ii, 'food_name', e.target.value)}
                                    className="w-full bg-gray-800 border-white/15 rounded-lg py-1.5 px-3 text-sm text-white focus:ring-2 focus:ring-marizan-red outline-none"
                                  />
                                </div>
                                <div className="md:col-span-3">
                                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1 leading-none">Canti.</label>
                                  <div className="flex">
                                    <input 
                                      type="number" value={item.quantity} onChange={(e) => updateItem(di, mi, ii, 'quantity', e.target.value)}
                                      className="w-full bg-gray-800 border-white/15 border-r-0 rounded-l-lg py-1.5 px-3 text-sm text-white focus:ring-2 focus:ring-marizan-red outline-none"
                                    />
                                    <select 
                                      value={item.unit} onChange={(e) => updateItem(di, mi, ii, 'unit', e.target.value)}
                                      className="bg-gray-800 border-white/15 border-l-0 rounded-r-lg py-1.5 px-2 text-xs text-gray-300 font-medium focus:ring-2 focus:ring-marizan-red outline-none"
                                    >
                                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                  </div>
                                </div>
                                <div className="md:col-span-4 flex gap-4">
                                  <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1 leading-none text-indigo-500">Kcal</label>
                                    <input 
                                      type="number" value={item.calories} onChange={(e) => updateItem(di, mi, ii, 'calories', e.target.value)}
                                      className="w-full bg-gray-800 border-white/15 rounded-lg py-1.5 px-3 text-sm text-white focus:ring-2 focus:ring-marizan-red outline-none"
                                    />
                                  </div>
                                  <div className="flex items-center pt-5">
                                    <button onClick={() => removeItem(di, mi, ii)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                                      <IoTrashOutline size={16} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mt-3">
                                <div>
                                  <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 ml-1 leading-none text-rose-300">Prot.(g)</label>
                                  <input 
                                    type="number" value={item.protein_g} onChange={(e) => updateItem(di, mi, ii, 'protein_g', e.target.value)}
                                    className="w-full bg-gray-800 border border-rose-500/30 rounded-lg py-1.5 px-3 text-xs text-white focus:ring-2 focus:ring-rose-400 outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 ml-1 leading-none text-cyan-300">Carb.(g)</label>
                                  <input 
                                    type="number" value={item.carbs_g} onChange={(e) => updateItem(di, mi, ii, 'carbs_g', e.target.value)}
                                    className="w-full bg-gray-800 border border-cyan-500/30 rounded-lg py-1.5 px-3 text-xs text-white focus:ring-2 focus:ring-cyan-400 outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 ml-1 leading-none text-amber-300">Gra.(g)</label>
                                  <input 
                                    type="number" value={item.fat_g} onChange={(e) => updateItem(di, mi, ii, 'fat_g', e.target.value)}
                                    className="w-full bg-gray-800 border border-amber-500/30 rounded-lg py-1.5 px-3 text-xs text-white focus:ring-2 focus:ring-amber-400 outline-none"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 flex flex-col md:flex-row items-center gap-4">
                          <button 
                            onClick={() => addItem(di, mi)}
                            className="bg-marizan-red/15 text-marizan-red hover:bg-marizan-red/25 px-4 py-2 rounded-xl text-sm font-bold flex items-center transition-colors w-full md:w-auto justify-center"
                          >
                            <IoAdd size={14} className="mr-1" /> Añadir alimento
                          </button>
                          <textarea 
                            value={meal.notes || ''} onChange={(e) => updateMeal(di, mi, 'notes', e.target.value)}
                            placeholder="Notas para esta comida..."
                            rows="1"
                            className="flex-1 bg-transparent border-none text-sm text-gray-300 focus:ring-0 w-full placeholder:italic"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button 
                    onClick={() => addMeal(di)}
                    className="w-full py-3 border-2 border-dashed border-white/20 rounded-2xl text-gray-300 font-bold hover:bg-white/10 hover:border-marizan-red/40 hover:text-white transition-all flex items-center justify-center p-6 bg-white/5 shadow-sm"
                  >
                    <IoAdd size={20} className="mr-2" /> Añadir comida
                  </button>
                </div>
              </div>
            )
          ))}

          <button 
            onClick={addDay}
            className="w-full mt-10 py-4 border-2 border-dashed border-white/20 rounded-2xl text-gray-200 font-bold hover:bg-white/10 transition-all flex items-center justify-center p-6 bg-white/5 shadow-sm"
          >
            <IoAdd size={24} className="mr-2" /> Añadir día al plan
          </button>
        </div>
      </div>
      
      {error && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-red-900/90 border border-red-500/60 text-red-100 px-6 py-3 rounded-xl shadow-2xl animate-bounce">
          {error}
        </div>
      )}
    </div>
  );
};

export default NutritionPlanEdit;
